import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const QuestionCard = ({ question, onAnswer, currentAnswer }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Set default answer of 1 if no answer exists yet
  useEffect(() => {
    if (currentAnswer === undefined || currentAnswer === null) {
      onAnswer(question?.id, 1);
    }
  }, [question?.id, currentAnswer, onAnswer]);

  const handleButtonClick = (value) => {
    onAnswer(question?.id, value);
  };

  const getScaleLabel = (value) => {
    const labels = {
      1: 'Not at all',
      2: 'Minimal',
      3: 'Developing',
      4: 'Proficient',
      5: 'Advanced'
    };
    return labels?.[value] || '';
  };

  const getScaleColor = (value) => {
    const colors = {
      1: 'text-red-500',
      2: 'text-orange-500',
      3: 'text-yellow-500',
      4: 'text-blue-500',
      5: 'text-green-500'
    };
    return colors?.[value] || 'text-muted-foreground';
  };

  const getButtonBgColor = (value, isSelected) => {
    if (!isSelected) return 'bg-surface hover:bg-gray-100';
    const colors = {
      1: 'bg-red-500 hover:bg-red-600',
      2: 'bg-orange-500 hover:bg-orange-600',
      3: 'bg-yellow-500 hover:bg-yellow-600',
      4: 'bg-blue-500 hover:bg-blue-600',
      5: 'bg-green-500 hover:bg-green-600'
    };
    return colors?.[value] || 'bg-secondary hover:bg-secondary/90';
  };

  return (
    <div className="bg-white rounded-lg shadow-subtle p-4 sm:p-6 mb-4 sm:mb-6 hover:shadow-elevation hover:scale-[1.02] transition-all duration-300">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="text-base sm:text-lg font-semibold text-primary mb-2">{question?.title}</h4>
          <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{question?.description}</p>
        </div>
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowTooltip(!showTooltip)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="p-2 text-muted-foreground hover:text-secondary hover:scale-110 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200 rounded-full"
            type="button"
          >
            <Icon name="HelpCircle" size={20} />
          </button>
          {showTooltip && (
            <>
              <div 
                className="fixed inset-0 z-10 sm:hidden" 
                onClick={() => setShowTooltip(false)}
              />
              <div className="absolute right-0 top-10 w-64 sm:w-72 bg-primary text-white p-3 rounded-lg shadow-elevation z-20 text-xs sm:text-sm max-w-[calc(100vw-2rem)] sm:max-w-none">
                {question?.tooltip}
                <div className="absolute -top-2 right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-primary"></div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs sm:text-sm text-muted-foreground">Rate your current state:</span>
          {currentAnswer && (
            <span className={`text-xs sm:text-sm font-medium ${getScaleColor(currentAnswer)}`}>
              {getScaleLabel(currentAnswer)}
            </span>
          )}
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-5 gap-2">
            {[1, 2, 3, 4, 5]?.map((value) => (
              <button
                key={value}
                onClick={() => handleButtonClick(value)}
                className={`
                  flex flex-col items-center justify-center p-3 sm:p-4 rounded-lg 
                  transition-all duration-200 border-2
                  focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2
                  active:scale-95
                  ${currentAnswer === value 
                    ? `${getButtonBgColor(value, true)} text-white border-transparent shadow-md scale-105` 
                    : 'bg-surface text-muted-foreground border-gray-200 hover:border-secondary hover:shadow-sm hover:scale-105'
                  }
                `}
                type="button"
              >
                <span className="text-lg sm:text-xl font-bold mb-1">{value}</span>
                <span className="text-[10px] sm:text-xs text-center leading-tight">
                  {getScaleLabel(value)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-between text-xs text-muted-foreground pt-2">
          <span>Not at all</span>
          <span>Advanced</span>
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;